import dayjs from "dayjs";

import { getCsrfToken } from "@phoarau/ts_sysop_utils";

import { UserNotification } from "../Models/UserNotification";

/**
 * Rendu et acquittement des notifications utilisateur, partages entre le
 * panneau de la cloche (common/Notifications.ts) et la page /notifications.
 *
 * Le rendu est construit en DOM (jQuery `.text()`) et non par interpolation de
 * chaines : `message` contient des donnees metier (plaques, noms de zone) qui
 * n'ont pas a etre interpretees comme du HTML.
 */

export type NotificationListeResponse = {
    recordsTotal: number;
    recordsFiltered: number;
    data: UserNotification[];
};

type FetchOptions = {
    start?: number;
    length?: number;
    unreadOnly?: boolean;
};

/** Recupere une page de notifications. */
export function fetchNotifications(options: FetchOptions = {}): JQuery.jqXHR<NotificationListeResponse> {
    const data: Record<string, string | number> = {
        start: options.start ?? 0,
        length: options.length ?? 20,
    };

    if (options.unreadOnly) {
        data.unread_only = "true";
    }

    return $.ajax({
        url: "/notifications/liste.json",
        type: "GET",
        cache: false,
        dataType: "json",
        data,
    });
}

/** Compteurs de la cloche : non lues (rail) et non vues (pastille). */
export function fetchNotificationCounts(): JQuery.jqXHR<{ unread: number; unviewed: number }> {
    return $.ajax({
        url: "/notifications/unread.json",
        type: "GET",
        cache: false,
        dataType: "json",
    });
}

function post(url: string, data?: Record<string, unknown>): JQuery.jqXHR {
    return $.ajax({
        url,
        type: "POST",
        dataType: "json",
        headers: { "X-CSRF-Token": getCsrfToken() },
        data,
    });
}

export function markNotificationRead(notificationId: number): JQuery.jqXHR {
    return post("/notifications/markRead.json", { id: notificationId });
}

export function markAllNotificationsRead(): JQuery.jqXHR {
    return post("/notifications/markAllRead.json");
}

/** Acquitte l'affichage du panneau : eteint la pastille sans marquer lu. */
export function markNotificationsViewed(): JQuery.jqXHR {
    return post("/notifications/markViewed.json");
}

/** « Aujourd'hui », « Hier », sinon la date en toutes lettres. */
function dayLabel(date: string): string {
    const day = dayjs(date).startOf("day");
    const today = dayjs().startOf("day");
    const diff = today.diff(day, "day");

    if (diff === 0) {
        return "Aujourd'hui";
    }

    if (diff === 1) {
        return "Hier";
    }

    return day.format("D MMMM YYYY");
}

/** Relatif dans la journee (« il y a 5 minutes »), heure au-dela. */
function timeLabel(date: string): string {
    const moment = dayjs(date);

    return moment.isAfter(dayjs().subtract(1, "day")) ? moment.fromNow() : moment.format("HH:mm");
}

function buildItem(notification: UserNotification, isRead: boolean): JQuery<HTMLElement> {
    const title = notification.notification?.subject ?? notification.notification?.type_notification?.name ?? "";

    // <time> et non <span> : le theme Volt force `width: 30px` sur tout span
    // d'un .dropdown-menu, ce qui tronquait la date et faisait deborder le
    // panneau horizontalement.
    const $date = $("<time>")
        .addClass("nf-item__date")
        .attr("datetime", notification.created)
        .text(timeLabel(notification.created));

    const $head = $('<div class="nf-item__head">').append($('<h3 class="nf-item__title">').text(title)).append($date);

    return $("<button>")
        .attr("type", "button")
        .addClass(`nf-item${isRead ? " nf-item--read" : ""}`)
        .attr("data-notification-id", String(notification.notification_id))
        .append($head)
        .append($('<p class="nf-item__message">').text(notification.message ?? ""));
}

export type EmptyState = {
    title: string;
    hint?: string;
    isError?: boolean;
};

export function renderEmptyState($container: JQuery<HTMLElement>, state: EmptyState): void {
    const $empty = $("<div>")
        .addClass(`nf-empty${state.isError ? " nf-empty--error" : ""}`)
        .append($('<p class="nf-empty__title">').text(state.title));

    if (state.hint) {
        $empty.append($('<p class="nf-empty__hint">').text(state.hint));
    }

    $container.empty().append($empty);
}

/**
 * Ajoute les notifications au conteneur, groupees par jour. Le separateur n'est
 * pose que lorsque le jour change — en ajout (« Charger les precedentes »), le
 * dernier jour deja rendu evite un doublon de separateur.
 */
export function appendNotifications($container: JQuery<HTMLElement>, notifications: UserNotification[]): void {
    let lastDay = $container.children(".nf-day").last().text();

    notifications.forEach((notification) => {
        const label = dayLabel(notification.created);

        if (label !== lastDay) {
            $container.append($('<div class="nf-day">').text(label));
            lastDay = label;
        }

        $container.append(buildItem(notification, notification.is_read === true));
    });
}
